<script setup>
import { ref, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useOrderStore } from '@/stores/OrderStore';
import { useProductStore } from '@/stores/productsStore';
import { useToast } from 'vue-toastification';

const authStore = useAuthStore();
const orderStore = useOrderStore();
const store = useProductStore();
const toast = useToast();

// Props to accept the item information
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isTaxChange: {
    type: Boolean,
    default: true
  }
});

const discountPercentage = ref(props.item?.discountPercentage ?? 0); 
const originalPrice = ref(props.item?.OriginalPrice ?? props.item.Price);
const comment = ref('');
const isSubmitting = ref(false);
const isCommentProvided = ref(false);  
const showErrorPopup = ref(false);
const errorMessage = ref('');

const emit = defineEmits(['close', 'commentSubmitted', 'resetPending']);

const managerNow = store.users.find(user => user.FullName === authStore.managerUser);
const isManagerAllowed = computed(() => {
  if (discountPercentage.value > managerNow.AllowedDicount || props.item.Discount > managerNow.AllowedDicount) {
    errorMessage.value = `Manager ${authStore.managerUser} cannot do discount more than ${managerNow.AllowedDicount}%`;
    showErrorPopup.value = true;
    return false;
  }
  return true;
});

// Submit comment
const submitComment = async () => {
  if (!isManagerAllowed.value) return;
  
  isSubmitting.value = true;
  orderStore.submitCommentToStore(
    comment.value,
    authStore.managerUser,
    props.item,
  );

  emit('commentSubmitted', comment.value);
  emit('close');
  isSubmitting.value = false;
};

// Handle cancel and reset the discount
const cancelComment = () => {
  orderStore.state.overallDiscount = 0;
  if (props.item) {
    if (!props.item.taxRates) {
      props.item.taxRates = orderStore.state.taxes.map(tax => ({
        type: tax.type,
        rate: tax.rate,
        amount: 0
      }));
    }
    orderStore.resetDiscount(props.item);
    orderStore.resetTaxRate(props.item);
  }
  emit('close');
  emit('resetPending');
};

// Watch for changes in isTaxChange to ensure reactivity
watch(() => props.isTaxChange, (newValue) => {
  console.log('isTaxChange changed:', newValue);
});

const imageBackgroundColor = computed(() => {
  return comment.value.trim() ? 'bg-green-400' : 'bg-red-400';
});

function closeErrorPopup() {
  showErrorPopup.value = false;
  errorMessage.value = '';
  cancelComment();
}
</script>

<template>
  <Teleport to="body">
    <!-- Error Popup -->
    <Transition name="fade">
      <div v-if="showErrorPopup" class="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
        <div class="bg-white rounded-lg shadow-lg p-6 max-w-sm mx-auto border border-red-400">
          <div class="flex items-center gap-3 mb-4">
            <div class="bg-red-100 p-2 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-red-600">Discount Limit Exceeded</h3>
          </div>
          <p class="text-gray-700 mb-4">{{ errorMessage }}</p>
          <button 
            @click="closeErrorPopup()" 
            class="w-full py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
          >
            OK
          </button>
        </div>
      </div>
    </Transition>

    <!-- Main Modal -->
    <Transition name="modal-outer">
      <div class="absolute w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center px-8">
        <Transition name="modal-inner" class="rounded-2xl">
          <div class="fixed top-10 z-40 flex items-center justify-center bg-black bg-opacity-50 w-10/12">
            <div class="collapse bg-white rounded-2xl p-6 w-full shadow-lg">
              <input type="checkbox" checked="checked" />

              <!-- Item Info -->
              <div class="collapse-title gap-4 mb-4">
                <h3 class="text-lg font-semibold mb-4">Manager Approval Comment</h3>
                <div class="flex justify-between">
                  <div class="flex">
                    <img v-if="props.item.ItemImage" :src="`${props.item?.ItemImage ?? ''}`"
                      class="w-14 rounded-lg" alt="product-img" />
                    <div class="flex flex-col">
                      <p class="font-semibold">{{ props.item?.ItemName ?? 'Overall Discount' }}</p>
                      <div class="flex items-center gap-12">
                        <div class="">
                          <p v-if="props.item.Price" class="text-sm text-gray-500">Price: ${{ props.item?.Price ?? 0 }}</p>
                          <p class="text-sm text-gray-500">Discount: {{ props.item?.Discount ?? discountPercentage }}%</p>
                        </div>
                        <div class="v">
                          <p class="text-sm text-gray-500">
                            {{ orderStore.state.taxes[0]?.type || 'Tax' }}: 
                            {{ props.item.taxRates?.[0]?.rate ?? orderStore.state.taxes[0]?.rate ?? 0 }}%
                          </p>
                          <p class="text-sm text-gray-500">
                            {{ orderStore.state.taxes[1]?.type || 'Tax' }}: 
                            {{ props.item.taxRates?.[1]?.rate ?? orderStore.state.taxes[1]?.rate ?? 0 }}%
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flex">
                    <p class="text-sm text-gray-500 mt-2">Approved by: <span class="font-semibold">{{ authStore.managerUser }}</span></p>
                    <img src="/message.svg" :class="imageBackgroundColor" class="rounded-md p-1" alt="Comment Icon">
                  </div>
                </div>
              </div>

              <!-- Comment Textarea -->
              <div class="collapse-content">
                <textarea 
                  v-model="comment" 
                  rows="4" 
                  class="w-full border rounded-lg p-2"
                  placeholder="Enter your comment"
                  :disabled="!isManagerAllowed"
                ></textarea>

                <div class="mt-4 flex justify-end gap-4">
                  <button 
                    @click="submitComment" 
                    :disabled="isSubmitting || !isManagerAllowed" 
                    class="px-4 py-2 bg-purple-500 text-white rounded-lg disabled:opacity-50"
                  >
                    <i class="pi pi-save"></i>
                  </button>
                  <button 
                    @click="cancelComment" 
                    class="px-4 py-2 bg-gray-300 rounded-lg"
                  >
                    <i class="pi pi-times"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-outer-enter-active,
.modal-outer-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-outer-enter-from,
.modal-outer-leave-to {
  opacity: 0;
}

.modal-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
}

.modal-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.modal-inner-leave-to {
  transform: scale(0.8);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>